import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerAuthContext = createContext(null);

export const useMasterPartnerAuth = () => {
  const context = useContext(MasterPartnerAuthContext);
  if (!context) {
    throw new Error('useMasterPartnerAuth must be used within a MasterPartnerAuthProvider');
  }
  return context;
};

export const MasterPartnerAuthProvider = ({ children }) => {
  const [masterPartner, setMasterPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('master_partner_token'));

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/master-partner/portal/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMasterPartner(response.data);
    } catch (error) {
      console.error('Token validation failed:', error);
      localStorage.removeItem('master_partner_token');
      setToken(null);
      setMasterPartner(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/master-partner/login`, { email, password });
      const { access_token, master_partner: masterPartnerData } = response.data;
      
      localStorage.setItem('master_partner_token', access_token);
      setToken(access_token);
      setMasterPartner(masterPartnerData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed. Please try again.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('master_partner_token');
    setToken(null);
    setMasterPartner(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const refreshProfile = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/master-partner/portal/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMasterPartner(response.data);
      } catch (error) {
        console.error('Profile refresh failed:', error);
      }
    }
  };

  const value = {
    masterPartner,
    loading,
    login,
    logout,
    getAuthHeader,
    refreshProfile,
    isAuthenticated: !!masterPartner
  };

  return (
    <MasterPartnerAuthContext.Provider value={value}>
      {children}
    </MasterPartnerAuthContext.Provider>
  );
};

export default MasterPartnerAuthContext;
